// Print utility for QR codes. Framework-agnostic — no React, no DOM-tree queries
// outside the helper. All callers pass plain data.
//
// Item shape:
//   { svgString, title, code, instructions, url, brand?, footer? }

const DEFAULT_BRAND = 'DEATH CARD'

// Helper: pull a rendered <svg> out of the DOM by its id and return as string.
// Use when the QR is already on screen (e.g. event QR tab).
export function serializeSvgFromDom(elementId) {
  const el = document.getElementById(elementId)
  if (!el) return null
  return new XMLSerializer().serializeToString(el)
}

// Trigger a PNG download of an SVG. Pass either an svg string or the DOM id
// of an inline <svg>. Filename is sanitized (no slashes/colons) and
// auto-suffixed with .png. Resolution defaults to 1024x1024 for crisp print
// scans even after rasterization.
export function downloadQRPng({ svgString, svgElementId, filename = 'qr-code', size = 1024 } = {}) {
  let svg = svgString
  if (!svg && svgElementId) svg = serializeSvgFromDom(svgElementId)
  if (!svg) {
    console.warn('[downloadQRPng] no SVG provided')
    return
  }
  // Make sure the svg root carries width/height — react-qr-code emits a
  // viewBox-only svg, which renders 0×0 in canvas drawImage on some browsers.
  if (!/\swidth=/.test(svg) || !/\sheight=/.test(svg)) {
    svg = svg.replace(/<svg(\s|>)/, `<svg width="${size}" height="${size}"$1`)
  }
  const safeName = String(filename).replace(/[\\/:*?"<>|]+/g, '_').slice(0, 96)
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const img = new Image()
  img.onload = () => {
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, size, size)
    ctx.drawImage(img, 0, 0, size, size)
    URL.revokeObjectURL(url)
    canvas.toBlob((png) => {
      if (!png) return
      const a = document.createElement('a')
      a.href = URL.createObjectURL(png)
      a.download = `${safeName}.png`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(a.href), 1000)
    }, 'image/png')
  }
  img.onerror = () => {
    URL.revokeObjectURL(url)
    console.warn('[downloadQRPng] image load failed')
  }
  img.src = url
}

// Page-level CSS shared across all print modes.
const SHARED_CSS = `
  @page { margin: 0.5in; }
  body { font-family: -apple-system, BlinkMacSystemFont, sans-serif; text-align: center; padding: 40px; margin: 0; color: #111827; background: #fff; }
  .container { max-width: 500px; margin: 0 auto; }
  .qr-wrapper { background: #fff; padding: 24px; border: 4px solid #f59e0b; border-radius: 16px; display: inline-block; margin: 24px 0; }
  .qr-wrapper svg { width: 320px; height: 320px; display: block; }
  h1 { font-size: 36px; margin: 0 0 8px; }
  .instructions { margin: 16px 0; color: #374151; font-size: 18px; }
  .label { color: #6b7280; font-size: 14px; margin: 16px 0 4px; text-transform: uppercase; letter-spacing: 2px; }
  .code { font-size: 48px; font-weight: bold; color: #f59e0b; letter-spacing: 8px; margin: 4px 0 16px; }
  .url { color: #6b7280; font-size: 14px; word-break: break-all; }
  .brand { margin-top: 32px; font-weight: bold; color: #f59e0b; letter-spacing: 4px; font-size: 14px; }
  .footer { color: #6b7280; font-size: 12px; margin-top: 8px; }
  .print-btn { margin-top: 24px; padding: 12px 24px; background: #f59e0b; color: #111827; font-weight: bold; border: none; border-radius: 8px; cursor: pointer; font-size: 16px; }
  .page { break-after: page; }
  .page:last-child { break-after: auto; }
  @media print {
    body { padding: 0; }
    .no-print { display: none; }
  }
`

// Render a single QR item to HTML (without page wrapping).
function renderItem(item) {
  const brand = item.brand ?? DEFAULT_BRAND
  const footer = item.footer ? `<p class="footer">${item.footer}</p>` : ''
  return `
    <div class="container">
      <h1>${escapeHtml(item.title)}</h1>
      <p class="instructions">${escapeHtml(item.instructions)}</p>
      <div class="qr-wrapper">${item.svgString}</div>
      <p class="label">Code</p>
      <p class="code">${escapeHtml(item.code)}</p>
      <p class="url">${escapeHtml(item.url)}</p>
      <p class="brand">${escapeHtml(brand)}</p>
      ${footer}
    </div>
  `
}

// Escape HTML to defend against accidental injection from event/player names.
function escapeHtml(str) {
  if (str == null) return ''
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

// Open the print window with the given inner HTML, trigger the print dialog.
function openPrintWindow(innerHtml, docTitle) {
  const w = window.open('', '_blank')
  if (!w) {
    alert('Print window blocked. Please allow popups for this site.')
    return
  }
  w.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>${escapeHtml(docTitle)}</title>
  <style>${SHARED_CSS}</style>
</head>
<body>
  ${innerHtml}
  <button class="no-print print-btn" onclick="window.print()">Print</button>
</body>
</html>`)
  w.document.close()
  setTimeout(() => w.print(), 300)
}

// Print a single QR. `item` must include svgString, title, code, instructions, url.
export function printQR(item) {
  if (!item?.svgString) return
  openPrintWindow(renderItem(item), item.title || 'QR Code')
}

// Print many QRs as a batch — each gets its own page break.
// `items` is an array of the same shape.
export function printQRBatch(items) {
  if (!items?.length) return
  const html = items.map(item => `<div class="page">${renderItem(item)}</div>`).join('')
  openPrintWindow(html, `${items.length} QR Codes`)
}

// ── BULK EXPORT (PDF grid + ZIP of PNGs) ───────────────────────────────
//
// Each item: { svgString, label, fileName }
//   svgString : already-serialized <svg> markup (caller is responsible for
//               rendering it via react-qr-code into the DOM and serializing
//               with serializeSvgFromDom)
//   label     : human-readable string drawn under the QR
//   fileName  : safe basename for the ZIP entry (no extension)

// Convert one SVG string to a PNG dataURL at the given pixel size.
function svgToPngDataUrl(svgString, size = 512) {
  return new Promise((resolve, reject) => {
    let svg = svgString
    if (!/\swidth=/.test(svg) || !/\sheight=/.test(svg)) {
      svg = svg.replace(/<svg(\s|>)/, `<svg width="${size}" height="${size}"$1`)
    }
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, size, size)
      ctx.drawImage(img, 0, 0, size, size)
      URL.revokeObjectURL(url)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e) }
    img.src = url
  })
}

// Build an A4-portrait PDF grid of QRs. 3 columns × 4 rows per page (12 per
// page) at 512×512 PNG per cell — readable when printed at 1.5×1.5 inches.
// Adds a small ANUBIS / DEATH CARD watermark at the page footer.
export async function downloadQRPdfGrid(items, { fileName = 'qr-codes', cols = 3, rows = 4, watermark = 'DEATH CARD · ANUBIS' } = {}) {
  if (!items?.length) return
  const { jsPDF } = await import('jspdf')
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
  const pageW = pdf.internal.pageSize.getWidth()
  const pageH = pdf.internal.pageSize.getHeight()
  const margin = 10
  const labelHeight = 6
  const cellW = (pageW - 2 * margin) / cols
  const cellH = (pageH - 2 * margin - labelHeight) / rows
  const qrSize = Math.min(cellW, cellH) - labelHeight - 4

  let cellIndex = 0
  for (const item of items) {
    if (cellIndex > 0 && cellIndex % (cols * rows) === 0) {
      pdf.addPage()
    }
    const slot = cellIndex % (cols * rows)
    const c = slot % cols
    const r = Math.floor(slot / cols)
    const x = margin + c * cellW + (cellW - qrSize) / 2
    const y = margin + r * cellH + 2

    try {
      const png = await svgToPngDataUrl(item.svgString, 512)
      pdf.addImage(png, 'PNG', x, y, qrSize, qrSize)
    } catch (err) {
      console.warn('[downloadQRPdfGrid] svg→png failed for', item.fileName, err)
    }
    if (item.label) {
      pdf.setFontSize(9)
      pdf.setTextColor(40, 40, 40)
      const labelY = y + qrSize + labelHeight - 1
      pdf.text(String(item.label).slice(0, 40), margin + c * cellW + cellW / 2, labelY, { align: 'center' })
    }
    cellIndex++
  }

  // Footer watermark on every page
  const total = pdf.getNumberOfPages()
  for (let p = 1; p <= total; p++) {
    pdf.setPage(p)
    pdf.setFontSize(7)
    pdf.setTextColor(160, 160, 160)
    pdf.text(`${watermark} · page ${p}/${total}`, pageW / 2, pageH - 4, { align: 'center' })
  }

  pdf.save(`${String(fileName).replace(/[\\/:*?"<>|]+/g, '_').slice(0, 96)}.pdf`)
}

// Bundle items as PNGs in a single ZIP. Filenames are sanitized.
export async function downloadQRZip(items, { fileName = 'qr-codes' } = {}) {
  if (!items?.length) return
  const { default: JSZip } = await import('jszip')
  const zip = new JSZip()
  for (const item of items) {
    try {
      const dataUrl = await svgToPngDataUrl(item.svgString, 1024)
      const base64 = dataUrl.split(',')[1]
      const safe = String(item.fileName || item.label || 'qr').replace(/[\\/:*?"<>|]+/g, '_').slice(0, 96)
      zip.file(`${safe}.png`, base64, { base64: true })
    } catch (err) {
      console.warn('[downloadQRZip] failed for', item.fileName, err)
    }
  }
  const blob = await zip.generateAsync({ type: 'blob' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${String(fileName).replace(/[\\/:*?"<>|]+/g, '_').slice(0, 96)}.zip`
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}